// src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { subscription, auth } from '../services/api';
import Alert from '../components/ui/Alert';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    currentPassword: '',
    newPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchProfileData();
    fetchCurrentPlan();
  }, []);

  const fetchProfileData = async () => {
    try {
      const response = await auth.getProfile();
      setFormData(prev => ({
        ...prev,
        email: response.data.email,
        displayName: response.data.displayName || ''
      }));
    } catch (error) {
      console.error('Error fetching profile data:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to load profile data' 
      });
    }
  };

  const fetchCurrentPlan = async () => {
    try {
      const response = await subscription.getCurrentPlan();
      setCurrentPlan(response.data);
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Update basic profile info
      await auth.updateProfile({
        displayName: formData.displayName
      });

      // Handle password change if both fields are filled
      if (formData.currentPassword && formData.newPassword) {
        await auth.changePassword(formData.currentPassword, formData.newPassword);
      }

      // Clear password fields after successful update
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: ''
      }));

      setMessage({ type: 'success', text: 'Profile updated successfully' });
      fetchProfileData(); // Refresh data
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.message || 'Failed to update profile' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Subscription Details Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Subscription Details</h2>
            <Button 
              onClick={() => navigate('/subscription')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {currentPlan?.active ? 'Change Plan' : 'Upgrade Now'}
            </Button>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-600">Current Plan</p>
                <p className="font-medium">{currentPlan?.name || 'No active plan'}</p>
              </div>
              <div>
                <p className="text-gray-600">Status</p>
                <p className={`font-medium ${currentPlan?.active ? 'text-green-600' : 'text-red-600'}`}>
                  {currentPlan?.active ? 'Active' : 'Inactive'}
                </p>
              </div>
              {currentPlan?.expiresAt && (
                <div className="col-span-2">
                  <p className="text-gray-600">Expires On</p>
                  <p className="font-medium">
                    {new Date(currentPlan.expiresAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Profile Settings Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold mb-6">Profile Settings</h2>
          {message.text && (
            <Alert 
              variant={message.type === 'error' ? 'destructive' : 'default'} 
              className="mb-4"
            >
              {message.text}
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block mb-2">Email</label>
              <Input
                type="email"
                value={formData.email}
                disabled
                className="bg-gray-50"
              />
              <p className="text-sm text-gray-500 mt-1">Email cannot be changed</p>
            </div>
            <div>
              <label className="block mb-2">Display Name</label>
              <Input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder="Enter your display name"
              />
            </div>
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">Change Password</h3>
              <div className="space-y-4">
                <div>
                  <label className="block mb-2">Current Password</label>
                  <Input
                    type="password"
                    value={formData.currentPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="block mb-2">New Password</label>
                  <Input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Enter new password"
                  />
                </div>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Profile'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;